import React from "react";

import { css, cx } from "@emotion/css";
import { createColumnHelper, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import { NurseInclusionPatient } from "@/models/NurseInclusionPatientModel.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { ActionsColumn } from "@/uiKit/molecules/InclusionCenter/NurseInclusion/NurseInclusionHistoryColumns/ActionsColumn.tsx";
import { BirthDateColumn } from "@/uiKit/molecules/InclusionCenter/NurseInclusion/NurseInclusionHistoryColumns/BirthDateColumn.tsx";
import { CreatedAtColumn } from "@/uiKit/molecules/InclusionCenter/NurseInclusion/NurseInclusionHistoryColumns/CreatedAtColumn.tsx";
import { DoctorNameColumn } from "@/uiKit/molecules/InclusionCenter/NurseInclusion/NurseInclusionHistoryColumns/DoctorNameColumn.tsx";
import { EmailColumn } from "@/uiKit/molecules/InclusionCenter/NurseInclusion/NurseInclusionHistoryColumns/EmailColumn.tsx";
import { InclusionStatusColumn } from "@/uiKit/molecules/InclusionCenter/NurseInclusion/NurseInclusionHistoryColumns/InclusionStatusColumn.tsx";
import { PatientNameColumn } from "@/uiKit/molecules/InclusionCenter/NurseInclusion/NurseInclusionHistoryColumns/PatientNameColumn.tsx";
import { NurseInclusionHistoryTableHeader } from "@/uiKit/molecules/InclusionCenter/NurseInclusion/NurseInclusionHistoryTableHeader.tsx";
import { MuiTable } from "@/uiKit/Table.tsx";

export type NurseInclusionHistoryTableProps = {
  className?: string;
};

const columnHelper = createColumnHelper<NurseInclusionPatient>();

const columnDefinitions = {
  default: {
    size: 100,
    minSize: 0,
    maxSize: 100,
  },
  createdAt: columnHelper.accessor("createdAt", {
    cell: CreatedAtColumn,
    header: () => <NurseInclusionHistoryTableHeader title="nurseInclusion.history.createdAt" />,
    maxSize: 15,
  }),
  doctor: columnHelper.display({
    id: "doctor",
    cell: DoctorNameColumn,
    header: () => <NurseInclusionHistoryTableHeader title="nurseInclusion.history.doctor" />,
    maxSize: 15,
  }),
  name: columnHelper.display({
    id: "name",
    cell: PatientNameColumn,
    header: () => <NurseInclusionHistoryTableHeader title="nurseInclusion.history.name" />,
    maxSize: 20,
  }),
  birthDate: columnHelper.accessor("birthDate", {
    cell: BirthDateColumn,
    header: () => <NurseInclusionHistoryTableHeader title="nurseInclusion.history.birthDate" />,
    maxSize: 15,
  }),
  email: columnHelper.accessor("email", {
    cell: EmailColumn,
    header: () => <NurseInclusionHistoryTableHeader title="nurseInclusion.history.email" />,
    maxSize: 20,
  }),
  status: columnHelper.accessor("status", {
    cell: InclusionStatusColumn,
    header: () => <NurseInclusionHistoryTableHeader title="nurseInclusion.history.status" />,
    maxSize: 12,
  }),
  actions: columnHelper.display({
    id: "actions",
    cell: ActionsColumn,
    header: () => null,
    maxSize: 13,
  }),
};

export const NurseInclusionHistoryTable: React.FC<NurseInclusionHistoryTableProps> = ({ className }) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const inclusionsList = Queries.practitioner.useNurseInclusionSuggestions({});
  const table = useReactTable<NurseInclusionPatient>({
    columns: [
      columnDefinitions.createdAt,
      columnDefinitions.doctor,
      columnDefinitions.name,
      columnDefinitions.birthDate,
      columnDefinitions.email,
      columnDefinitions.status,
      columnDefinitions.actions,
    ],
    data: inclusionsList,
    getCoreRowModel: getCoreRowModel(),
    getRowId: row => row.id,
    enableRowSelection: false,
  });

  if (!inclusionsList) {
    return (
      <Typography variant="body" className={styles.noContent}>
        {t("nurseInclusion.history.noInclusion")}
      </Typography>
    );
  }

  return (
    <div className={cx(styles.container, className)}>
      <MuiTable
        id="nurse-inclusion-history-table"
        table={table}
        classes={{
          headerCell: styles.header,
          cell: styles.cell,
        }}
      />
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({}),
  noContent: css({
    fontStyle: "italic",
  }),
  header: css({
    padding: theme.spacing(2, 8),
    whiteSpace: "nowrap",
    minWidth: 0,
    overflow: "clip",
  }),
  cell: css({
    height: theme.spacing(22),
    padding: theme.spacing(2, 8),
    whiteSpace: "nowrap",
    minWidth: 0,
    overflow: "clip",
  }),
});
